import { Badge, cn } from "@gotts.ai/ui";
import { Coins, Diamond, Factory, FileCode2, Route, Star } from "lucide-react";

import { Address } from "../../components/Address";
import { PcbCard } from "../../components/PcbCard";

import { GROUP_COLORS } from "./types";
import { useContractMeta } from "./useContractMeta";

import type { ContractOption } from "./types";

interface ContractHeaderProps {
  contract: ContractOption;
  walletAddress: string | null;
  isFavorite: boolean;
  onToggleFavorite: () => void;
}

const TYPE_ICONS: Record<string, typeof FileCode2> = {
  "ERC-20": Coins,
  Factory: Factory,
  Router: Route,
  WETH: Coins,
};

export function ContractHeader({
  contract,
  walletAddress,
  isFavorite,
  onToggleFavorite,
}: ContractHeaderProps) {
  const Icon = TYPE_ICONS[contract.contractType] ?? FileCode2;
  const meta = useContractMeta(contract.address);

  return (
    <PcbCard label={contract.group.toUpperCase()} cut={10}>
      <div className="flex flex-wrap items-start gap-4 sm:flex-nowrap">
        {/* Icon */}
        <div
          className={cn(
            "flex h-12 w-12 shrink-0 items-center justify-center",
            "border border-[rgba(201,168,76,0.15)] bg-[rgba(201,168,76,0.06)]",
          )}
        >
          <Icon size={22} className="text-[var(--color-gold)]" />
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1 space-y-2">
          {/* Name + badges */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-sans text-base font-semibold text-[var(--color-text)]">
              {contract.label}
            </span>
            <Badge
              className={cn(
                "text-[9px]",
                GROUP_COLORS[contract.group] ?? "bg-gray-500/20 text-gray-400",
              )}
            >
              {contract.group}
            </Badge>
            <Badge className="text-[9px]">{contract.contractType}</Badge>
          </div>

          {/* Address */}
          <div className="flex items-center gap-2">
            <Address
              address={contract.address}
              leadChars={10}
              tailChars={8}
              fontSize={11}
              navigable
            />
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap items-center gap-4 text-xs">
            {meta.balance !== null && (
              <span className="octa-sm flex items-center gap-1.5 bg-[rgba(52,209,106,0.06)] px-2.5 py-1 font-mono text-[var(--color-success)]">
                <Diamond size={10} className="shrink-0" />
                {meta.balance} ETH
              </span>
            )}
            {meta.codeSize !== null && (
              <span className="octa-sm bg-[rgba(255,255,255,0.03)] px-2.5 py-1 font-mono text-[var(--color-text-muted)]">
                {meta.codeSize > 0
                  ? `${meta.codeSize.toLocaleString()} bytes`
                  : "EOA"}
              </span>
            )}
            <button
              type="button"
              onClick={onToggleFavorite}
              className={cn(
                "octa-sm flex items-center gap-1.5 px-2.5 py-1 transition-all",
                isFavorite
                  ? "bg-[rgba(234,179,8,0.1)] text-yellow-400"
                  : "bg-[rgba(255,255,255,0.03)] text-[var(--color-text-muted)] hover:bg-[rgba(234,179,8,0.06)] hover:text-yellow-400",
              )}
              title={isFavorite ? "Unpin" : "Pin"}
            >
              <Star size={11} className={cn(isFavorite && "fill-yellow-400")} />
              <span className="text-[10px]">
                {isFavorite ? "Pinned" : "Pin"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Signing identity */}
      {walletAddress && (
        <div className="mt-3 border-t border-[rgba(201,168,76,0.08)] pt-2.5 text-[10px] text-[var(--color-text-muted)]">
          Signing as:{" "}
          <Address
            address={walletAddress as `0x${string}`}
            fontSize={10}
            leadChars={6}
            tailChars={4}
          />
        </div>
      )}
    </PcbCard>
  );
}
